import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ManagerCallAction } from "@regal-voice/shared-types";
import isNil from "lodash/isNil";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { ThunkAction } from "Services/state/StoreThunkTypes";
import { selectTasks } from "Services/state/tasks/Selectors";

import type { CallStatusEvent } from "Types/CallStatusEvent";

export interface ParticipantInfo {
    callSid: string | undefined;
    conferenceFriendlyId: string | undefined;
    conferenceSid: string | undefined;
    phoneNumber: string | undefined;
    startTimestamp: number | undefined;
    status: CallStatusEvent | undefined;
    name?: string;
    type: "contact" | "external" | "internal" | "external-phonebook";
    onHold?: boolean;
    isMuted?: boolean;
    from: string;
    sequenceNo: string;
    to: string;
    taskSid: string;
    queueSid?: string;
    queueName?: string;
    managerAction?: ManagerCallAction;
}

export type ConferenceEntryParticipants = {
    [sid: string]: ParticipantInfo;
};

export interface ConferenceEntry {
    deviceCallSid: string | undefined;
    connectionParams: Record<string, string | undefined>;
    conferenceFriendlyId: string | undefined;
    conferenceSid: string | undefined;
    isRecording: boolean | undefined;
    connected: boolean;
    participants: ConferenceEntryParticipants;
    duration: number;
}

export type ConferenceState = {
    value: ConferenceEntry;
};

const initialState: ConferenceState = {
    value: {
        deviceCallSid: undefined,
        conferenceFriendlyId: undefined,
        conferenceSid: undefined,
        isRecording: undefined,
        connected: false,
        participants: {},
        duration: 0,
        connectionParams: { taskSid: undefined },
    },
};

const ConferenceStateSlice = createSlice({
    name: "activeConference",
    initialState,
    reducers: {
        // after the rolloutUseConferenceStateSse flag is removed
        // these can probably be cleaned up some. I dont think we need ALL OF these values.
        // I think it is JUST the ConnectionParams taskSid and Params that we need to get the
        // active task for calls where we dont have a conference snapshot yet
        setConference(state, action: PayloadAction<Partial<ConferenceEntry>>): ConferenceState {
            return {
                ...state,
                value: {
                    ...state.value,
                    deviceCallSid: action.payload.deviceCallSid ?? state.value.deviceCallSid,
                    conferenceFriendlyId: action.payload.conferenceFriendlyId,
                    conferenceSid: action.payload.conferenceSid,
                    participants: action.payload.participants,
                    connected: action.payload.connected,
                    isRecording: isNil(state.value.isRecording) ? action.payload.isRecording : state.value.isRecording,
                } as ConferenceEntry,
            };
        },
        setDeviceParams(state, action: PayloadAction<{ taskSid: string | undefined }>) {
            return {
                ...state,
                value: {
                    ...state.value,
                    connectionParams: action.payload,
                },
            };
        },
        setConferenceTaskSid(state, action: PayloadAction<{ taskSid?: string }>) {
            const updatedConnectionParams = { ...state.value.connectionParams, ...action.payload };
            state.value.connectionParams = updatedConnectionParams;
        },
        clearConference: () => {
            return initialState;
        },
        // do the clearConference + setConferenceTaskSid so that we dont get a ui stutter on incoming calls
        resetConferenceForAcceptance(state, action: PayloadAction<{ taskSid?: string }>) {
            const updatedConnectionParams = { ...action.payload };
            return {
                ...initialState,
                value: {
                    ...initialState.value,
                    connectionParams: updatedConnectionParams,
                },
            };
        },

        ////////////////////////////////////////////////

        setConferenceDuration(state, action: PayloadAction<{ duration: number }>) {
            state.value.duration = action.payload.duration;
        },
    },
});

export const {
    actions: {
        clearConference,
        setConference,
        setConferenceTaskSid,
        resetConferenceForAcceptance,
        setDeviceParams,
        setConferenceDuration,
    },
    name,
    reducer,
} = ConferenceStateSlice;

export function selectActiveCallSid({ conference }: RootState) {
    return conference[ConferenceStateSlice.name].value.deviceCallSid;
}

export function setActiveConferenceTask({ id }: { id?: string }): ThunkAction<void, RootState, unknown, AnyAction> {
    return function thunk(dispatch: ThunkDispatch<RootState, unknown, AnyAction>, getState: () => RootState): void {
        let taskSid;
        if (id) {
            const task = selectTasks(getState())[id];
            taskSid = task?.taskSid;
        }
        dispatch(setConferenceTaskSid({ taskSid }));
    };
}

// resetConferenceForAcceptance combines the setConferenceTaskSid and the clearConference actions
// so that we dont get a a ui stutter on incoming calls
export function resetActiveConferenceTask({ id }: { id: string }): ThunkAction<void, RootState, unknown, AnyAction> {
    return function thunk(dispatch: ThunkDispatch<RootState, unknown, AnyAction>, getState: () => RootState): void {
        const task = selectTasks(getState())[id];
        const taskSid = task?.taskSid;
        dispatch(resetConferenceForAcceptance({ taskSid }));
    };
}
